import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

const BASE = '/api/chat';

export type ConversationType = 'dm' | 'group' | 'department' | 'company';

export interface ChatConversation {
  id: string;
  type: ConversationType;
  name: string;
  department: string;
  memberCount: number | null;
  isAdmin: boolean;
  lastMessageAt: string | null;
  lastMessagePreview: string;
  lastMessageByName: string;
  unreadCount: number;
}

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  content: string;
  createdAt: string;
  mine: boolean;
}

export const chatKeys = {
  root: ['chat'] as const,
  conversations: ['chat', 'conversations'] as const,
  messages: (id: string | null) => ['chat', 'messages', id] as const,
};

export function useChatConversations() {
  return useQuery({
    queryKey: chatKeys.conversations,
    queryFn: async () =>
      (await api.get<{ conversations: ChatConversation[] }>(`${BASE}/conversations`)).data.conversations,
    refetchInterval: 5_000,
  });
}

export function useChatMessages(conversationId: string | null) {
  return useQuery({
    queryKey: chatKeys.messages(conversationId),
    queryFn: async () =>
      (await api.get<{ messages: ChatMessage[]; hasMore: boolean }>(
        `${BASE}/conversations/${conversationId}/messages?limit=100`,
      )).data,
    enabled: !!conversationId,
    refetchInterval: 3_000,
  });
}

export function useChatMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: chatKeys.root });

  const startDm = useMutation({
    mutationFn: async (userId: string) =>
      (await api.post<{ id: string; name: string }>(`${BASE}/conversations`, { type: 'dm', userId })).data,
    onSuccess: refresh,
  });

  const createGroup = useMutation({
    mutationFn: async (payload: { name: string; memberIds: string[] }) =>
      (await api.post<{ id: string; name: string }>(`${BASE}/conversations`, { type: 'group', ...payload })).data,
    onSuccess: refresh,
  });

  const sendMessage = useMutation({
    mutationFn: async ({ conversationId, content }: { conversationId: string; content: string }) =>
      (await api.post<ChatMessage>(`${BASE}/conversations/${conversationId}/messages`, { content })).data,
    onSuccess: (_message, variables) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.messages(variables.conversationId) });
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations });
    },
  });

  const markRead = useMutation({
    mutationFn: async (conversationId: string) =>
      (await api.post(`${BASE}/conversations/${conversationId}/read`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chatKeys.conversations }),
  });

  return { startDm, createGroup, sendMessage, markRead };
}
